import { useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { getOrRefreshToken } from "@/lib/api";
import { GlassCard } from "@/components/ui";

// ════════════════════════════════════════════════════════════════════════════
// AgentConfirmationCard — Rendered inline for pending write actions
// ════════════════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL ?? "";

interface ConfirmationPreview {
  title: string;
  details: string[];
  isIrreversible?: boolean;
}

interface ConfirmationCardProps {
  actionId: string;
  actionType?: string;
  preview: ConfirmationPreview;
}

export function AgentConfirmationCard({ actionId, preview }: ConfirmationCardProps) {
  const [state, setState] = useState<"pending" | "loading" | "confirmed" | "cancelled" | "failed">("pending");
  const [resultMsg, setResultMsg] = useState("");

  const confirm = async () => {
    setState("loading");
    try {
      const token = await getOrRefreshToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/agent/confirm/${actionId}`, {
        method: "POST",
        headers,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error((err as { error?: string }).error ?? "Failed");
      }

      const data = await res.json() as { result?: { message?: string } };
      setResultMsg(data.result?.message ?? "Action completed.");
      setState("confirmed");
    } catch (e) {
      setResultMsg(e instanceof Error ? e.message : "Action failed.");
      setState("failed");
    }
  };

  const cancel = async () => {
    setState("loading");
    try {
      const token = await getOrRefreshToken();
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      await fetch(`${API_BASE}/api/agent/cancel/${actionId}`, {
        method: "POST",
        headers,
      });
    } catch {
      // ignore
    }
    setState("cancelled");
  };

  if (state === "confirmed") {
    return (
      <GlassCard className="rounded-xl px-4 py-3 border border-green-500/20 bg-green-500/5 max-w-xs">
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span className="text-xs font-medium">Done</span>
        </div>
        {resultMsg && <p className="text-xs text-af-600 dark:text-af-400 mt-1">{resultMsg}</p>}
      </GlassCard>
    );
  }

  if (state === "cancelled") {
    return (
      <GlassCard className="rounded-xl px-4 py-3 border border-af-200/30 max-w-xs">
        <div className="flex items-center gap-2 text-af-400">
          <XCircle className="w-4 h-4 shrink-0" />
          <span className="text-xs">Cancelled</span>
        </div>
      </GlassCard>
    );
  }

  if (state === "failed") {
    return (
      <GlassCard className="rounded-xl px-4 py-3 border border-red-500/20 bg-red-500/5 max-w-xs">
        <div className="flex items-center gap-2 text-red-500">
          <XCircle className="w-4 h-4 shrink-0" />
          <span className="text-xs font-medium">Failed</span>
        </div>
        {resultMsg && <p className="text-xs text-red-400 mt-1">{resultMsg}</p>}
      </GlassCard>
    );
  }

  return (
    <GlassCard className="rounded-xl border border-white/20 p-4 max-w-xs space-y-3">
      <div className="flex items-start gap-2">
        {preview.isIrreversible ? (
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        ) : (
          <CheckCircle2 className="w-4 h-4 text-af-600 dark:text-af-400 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <p className="text-xs font-semibold text-af-900 dark:text-af-100">{preview.title}</p>
          {preview.isIrreversible && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">This action cannot be undone.</p>
          )}
        </div>
      </div>

      <ul className="space-y-1">
        {preview.details.map((d, i) => (
          <li key={i} className="text-xs text-af-700 dark:text-af-300 pl-2 border-l border-white/10">{d}</li>
        ))}
      </ul>

      <div className="flex gap-2 pt-1">
        <button
          onClick={confirm}
          disabled={state === "loading"}
          className="flex-1 py-1.5 rounded-lg bg-af-900 dark:bg-af-100 text-white dark:text-af-900 text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1"
        >
          {state === "loading" ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            "Confirm"
          )}
        </button>
        <button
          onClick={cancel}
          disabled={state === "loading"}
          className="flex-1 py-1.5 rounded-lg border border-white/20 text-af-700 dark:text-af-300 text-xs font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </GlassCard>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Helper: extract pending confirmation from a message's tool-invocation parts
// ════════════════════════════════════════════════════════════════════════════

interface ToolInvocationPart {
  type: "tool-invocation";
  toolInvocation: {
    state: "call" | "result" | "partial-call";
    toolName: string;
    toolCallId: string;
    args?: unknown;
    result?: unknown;
  };
}

interface ConfirmationResult {
  status: string;
  action_id: string;
  action_type: string;
  preview: ConfirmationPreview;
}

export function extractConfirmation(parts: unknown[]): ConfirmationResult | null {
  for (const part of parts) {
    const p = part as ToolInvocationPart;
    if (p.type !== "tool-invocation") continue;
    const inv = p.toolInvocation;
    if (inv.state !== "result") continue;
    const r = inv.result as ConfirmationResult | undefined;
    if (r?.status === "confirmation_required" && r.action_id) {
      return r;
    }
  }
  return null;
}
